import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Boxes, X } from "lucide-react";

const MAX_NAME = 60;

type Props = {
    isOpen: boolean;
    // Prefilled suggestion the therapist can overwrite (e.g. board title/date).
    defaultName?: string;
    busy?: boolean;
    onCancel: () => void;
    onConfirm: (name: string, description: string, shareWithCenter: boolean) => void;
    // Portal target so the modal renders over the immersive/fullscreen board.
    portalContainer?: HTMLElement | null;
    /** The therapist's centre, when they are in one: they may save for it. */
    centerName?: string | null;
};

/** Modal to name and save the current board as a reusable scene (issue #197).
 *  The therapist types the name; it is prefilled, required and length-bounded.
 *  Cancelling saves nothing. Self-portals so it shows over the fullscreen board. */
export default function SaveSceneModal({
    isOpen,
    defaultName = "",
    busy,
    onCancel,
    onConfirm,
    portalContainer,
    centerName,
}: Props) {
    const { t } = useTranslation();
    const [name, setName] = useState(defaultName);
    const [description, setDescription] = useState("");
    const [touched, setTouched] = useState(false);
    const [forCenter, setForCenter] = useState(false);

    // Reset to the suggestion each time the modal opens.
    useEffect(() => {
        if (isOpen) {
            setName(defaultName);
            setDescription("");
            setTouched(false);
            // Personal unless chosen otherwise: sharing with the team is a
            // decision, never the default.
            setForCenter(false);
        }
    }, [isOpen, defaultName]);

    if (!isOpen) return null;

    const trimmed = name.trim();
    const error = trimmed.length === 0
        ? t("constellation.scenes.nameRequired")
        : trimmed.length > MAX_NAME
          ? t("constellation.scenes.nameTooLong")
          : null;

    const submit = () => {
        setTouched(true);
        if (error) return;
        onConfirm(trimmed, description.trim(), Boolean(centerName) && forCenter);
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
            onClick={onCancel}
        >
            <div
                className="w-full max-w-md rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={t("constellation.scenes.saveThis")}
            >
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="flex items-center gap-2 text-base font-semibold text-gray-800">
                        <Boxes size={18} /> {t("constellation.scenes.saveThis")}
                    </h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label={t("common.close", "Close")}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                        <X size={16} />
                    </button>
                </div>

                <label className="mb-1 block text-xs font-semibold text-gray-500">
                    {t("constellation.scenes.name")}
                </label>
                <input
                    autoFocus
                    type="text"
                    value={name}
                    maxLength={MAX_NAME}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") submit();
                    }}
                    placeholder={t("constellation.scenes.namePlaceholder")}
                    aria-invalid={touched && !!error}
                    className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100"
                />
                {touched && error && (
                    <p role="alert" className="mt-1 text-xs text-red-600">
                        {error}
                    </p>
                )}

                <label className="mb-1 mt-3 block text-xs font-semibold text-gray-500">
                    {t("constellation.scenes.description")}
                </label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-md border px-3 py-2 text-sm outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100"
                />

                {centerName && (
                    <fieldset className="mt-3">
                        <legend className="mb-1 block text-xs font-semibold text-gray-500">
                            {t("constellation.scenes.saveFor")}
                        </legend>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { value: false, label: t("constellation.scenes.personal") },
                                {
                                    value: true,
                                    label: t("constellation.scenes.forCenter", {
                                        center: centerName,
                                    }),
                                },
                            ].map((option) => (
                                <label
                                    key={String(option.value)}
                                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                                        forCenter === option.value
                                            ? "border-brand-400 bg-brand-50 text-brand-700"
                                            : "text-gray-600 hover:bg-gray-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="scene-scope"
                                        checked={forCenter === option.value}
                                        onChange={() => setForCenter(option.value)}
                                        className="accent-brand-600"
                                    />
                                    <span className="truncate">{option.label}</span>
                                </label>
                            ))}
                        </div>
                        {forCenter && (
                            <p className="mt-2 text-xs text-gray-500">
                                {t("constellation.scenes.centerHint")}
                            </p>
                        )}
                    </fieldset>
                )}

                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="rounded-md border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {t("constellation.scenes.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                        {busy ? t("constellation.scenes.saving") : t("constellation.scenes.confirm")}
                    </button>
                </div>
            </div>
        </div>,
        portalContainer ?? document.body,
    );
}
